import React, { useState, useEffect } from 'react';
import { useCompany } from '../context/CompanyContext';
import SenditDistrictPicker from '../components/Common/SenditDistrictPicker';
import '../components/Common/Common.css';

function CompanySettings() {
  const {
    companySettings,
    loading: contextLoading,
    updateCompanySettings,
    uploadLogo,
    deleteLogo,
    getLogoUrl,
    uploadHomepageBannerImage,
    deleteHomepageBannerImage,
    uploadHomepageBannerVideo,
    deleteHomepageBannerVideo,
    uploadB2bHeroVideo,
    deleteB2bHeroVideo,
  } = useCompany();

  const defaultOpeningTimes = [
    { days: 'Lundi - Vendredi', hours: '09h00 - 19h00', closed: false },
    { days: 'Samedi', hours: '09h00 - 19h00', closed: false },
    { days: 'Dimanche', hours: '', closed: true },
  ];

  const defaultQuickLinks = [
    { label: 'Nos Produits', url: '/products' },
    { label: 'Packs Promo', url: '/promo-packs' },
    { label: 'Espace B2B', url: '/b2b' },
    { label: 'À Propos', url: '/about' },
    { label: 'Contact', url: '/contact' },
    { label: 'Vérifier Garantie', url: '/warranty-check' },
  ];

  const defaultFooterCategories = [
    { label: 'Promotions', url: '/products?on_sale=true' },
    { label: 'Meilleures Ventes', url: '/products?is_best_seller=true' },
  ];

  const PIXEL_PROVIDERS = [
    { value: 'facebook_pixel',     label: 'Meta / Facebook Pixel', placeholder: 'ex: 1234567890123456' },
    { value: 'google_analytics',   label: 'Google Analytics (GA4)', placeholder: 'ex: G-XXXXXXXXXX' },
    { value: 'google_tag_manager', label: 'Google Tag Manager',     placeholder: 'ex: GTM-XXXXXXX' },
    { value: 'tiktok_pixel',       label: 'TikTok Pixel',           placeholder: 'ex: ABCD123456789' },
    { value: 'snapchat_pixel',     label: 'Snapchat Pixel',         placeholder: 'ex: 12345678-1234-1234-1234-123456789012' },
    { value: 'custom',             label: 'HTML personnalisé (avancé)', placeholder: '' },
  ];

  const PIXEL_PAGES = [
    { value: 'all',          label: 'Toutes les pages' },
    { value: 'home',         label: "Accueil" },
    { value: 'products',     label: 'Catalogue produits' },
    { value: 'product',      label: 'Fiche produit' },
    { value: 'promo_packs',  label: 'Catalogue packs promo' },
    { value: 'promo_pack',   label: 'Fiche pack promo' },
    { value: 'cart',         label: 'Panier' },
    { value: 'checkout',     label: 'Commande / checkout' },
    { value: 'b2b',          label: 'Espace B2B' },
    { value: 'about',        label: 'À propos' },
    { value: 'contact',      label: 'Contact' },
    { value: 'profile',      label: 'Profil client' },
    { value: 'auth',         label: 'Connexion / inscription' },
    { value: 'order_status', label: 'Suivi de commande' },
    { value: 'warranty',     label: 'Vérification garantie' },
  ];

  const makePixelId = () => `px_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

  const defaultPixel = () => ({
    id: makePixelId(),
    name: '',
    provider: 'facebook_pixel',
    pixel_id: '',
    custom_head: '',
    custom_body: '',
    pages: ['all'],
    enabled: true,
  });

  const [settings, setSettings] = useState({
    company_name: '',
    company_email: '',
    company_phone: '',
    company_address: '',
    address_de_facturation: '',
    company_city: '',
    company_country: '',
    company_website: '',
    google_maps_embed_url: '',
    tax_id: '',
    affiliation_cnss: '',
    currency: 'USD',
    currency_symbol: '$',
    facebook_url: '',
    instagram_url: '',
    whatsapp_number: '',
    youtube_url: '',
    tiktok_url: '',
    opening_times: defaultOpeningTimes,
    footer_description: '',
    footer_quick_links: defaultQuickLinks,
    footer_categories: defaultFooterCategories,
    tracking_pixels: [],
    sendit_pickup_district_id: '',
    sendit_pickup_district_name: '',
    sendit_pickup_district_price: '',
    free_shipping_threshold: '',
  });
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingBannerImage, setUploadingBannerImage] = useState(false);
  const [uploadingBannerVideo, setUploadingBannerVideo] = useState(false);
  const [uploadingB2bVideo, setUploadingB2bVideo] = useState(false);

  useEffect(() => {
    if (companySettings) {
      setSettings({
        company_name: companySettings.company_name || '',
        company_email: companySettings.company_email || '',
        company_phone: companySettings.company_phone || '',
        company_address: companySettings.company_address || '',
        address_de_facturation: companySettings.address_de_facturation || '',
        company_city: companySettings.company_city || '',
        company_country: companySettings.company_country || '',
        company_website: companySettings.company_website || '',
        google_maps_embed_url: companySettings.google_maps_embed_url || '',
        tax_id: companySettings.tax_id || '',
        affiliation_cnss: companySettings.affiliation_cnss || '',
        currency: companySettings.currency || 'USD',
        currency_symbol: companySettings.currency_symbol || '$',
        facebook_url: companySettings.facebook_url || '',
        instagram_url: companySettings.instagram_url || '',
        whatsapp_number: companySettings.whatsapp_number || '',
        youtube_url: companySettings.youtube_url || '',
        tiktok_url: companySettings.tiktok_url || '',
        opening_times: companySettings.opening_times || defaultOpeningTimes,
        footer_description: companySettings.footer_description || '',
        footer_quick_links: companySettings.footer_quick_links || defaultQuickLinks,
        footer_categories: companySettings.footer_categories || defaultFooterCategories,
        tracking_pixels: Array.isArray(companySettings.tracking_pixels) ? companySettings.tracking_pixels : [],
        sendit_pickup_district_id: companySettings.sendit_pickup_district_id || '',
        sendit_pickup_district_name: companySettings.sendit_pickup_district_name || '',
        sendit_pickup_district_price: companySettings.sendit_pickup_district_price ?? '',
        free_shipping_threshold: companySettings.free_shipping_threshold ?? '',
      });
    }
  }, [companySettings]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpeningTimeChange = (index, field, value) => {
    setSettings((prev) => {
      const updated = [...prev.opening_times];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, opening_times: updated };
    });
  };

  const addOpeningTimeRow = () => {
    setSettings((prev) => ({
      ...prev,
      opening_times: [...prev.opening_times, { days: '', hours: '', closed: false }],
    }));
  };

  const removeOpeningTimeRow = (index) => {
    setSettings((prev) => ({
      ...prev,
      opening_times: prev.opening_times.filter((_, i) => i !== index),
    }));
  };

  const handleFooterLinkChange = (listKey, index, field, value) => {
    setSettings((prev) => {
      const updated = [...prev[listKey]];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, [listKey]: updated };
    });
  };

  const addFooterLink = (listKey) => {
    setSettings((prev) => ({
      ...prev,
      [listKey]: [...(prev[listKey] || []), { label: '', url: '' }],
    }));
  };

  const removeFooterLink = (listKey, index) => {
    setSettings((prev) => ({
      ...prev,
      [listKey]: prev[listKey].filter((_, i) => i !== index),
    }));
  };

  // ── Tracking pixels ──────────────────────────────────────────────────────
  const addPixel = () => {
    setSettings((prev) => ({
      ...prev,
      tracking_pixels: [...(prev.tracking_pixels || []), defaultPixel()],
    }));
  };

  const removePixel = (index) => {
    setSettings((prev) => ({
      ...prev,
      tracking_pixels: prev.tracking_pixels.filter((_, i) => i !== index),
    }));
  };

  const updatePixel = (index, field, value) => {
    setSettings((prev) => {
      const next = [...prev.tracking_pixels];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, tracking_pixels: next };
    });
  };

  const togglePixelPage = (index, page) => {
    setSettings((prev) => {
      const next = [...prev.tracking_pixels];
      const p = { ...next[index] };
      const pages = new Set(Array.isArray(p.pages) ? p.pages : []);
      if (page === 'all') {
        if (pages.has('all')) {
          pages.delete('all');
        } else {
          pages.clear();
          pages.add('all');
        }
      } else {
        pages.delete('all');
        if (pages.has(page)) pages.delete(page);
        else pages.add(page);
      }
      p.pages = Array.from(pages);
      next[index] = p;
      return { ...prev, tracking_pixels: next };
    });
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Veuillez sélectionner un fichier image');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('La taille de l\'image doit être inférieure à 2 Mo');
      return;
    }

    setUploadingLogo(true);
    setError('');
    setMessage('');

    try {
      await uploadLogo(file);
      setMessage('Logo téléchargé avec succès !');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Échec du téléchargement du logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleBannerImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Veuillez sélectionner un fichier image');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("La taille de l'image doit être inférieure à 10 Mo");
      return;
    }
    setUploadingBannerImage(true);
    setError('');
    setMessage('');
    try {
      await uploadHomepageBannerImage(file);
      setMessage("Bannière (image) téléchargée avec succès !");
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Échec du téléchargement de la bannière");
    } finally {
      setUploadingBannerImage(false);
      e.target.value = '';
    }
  };

  const handleBannerImageDelete = async () => {
    if (!window.confirm("Supprimer la bannière (image) d'accueil ?")) return;
    setUploadingBannerImage(true);
    setError('');
    setMessage('');
    try {
      await deleteHomepageBannerImage();
      setMessage('Bannière supprimée');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Échec de la suppression');
    } finally {
      setUploadingBannerImage(false);
    }
  };

  const handleBannerVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      setError('Veuillez sélectionner un fichier vidéo');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setError("La taille de la vidéo doit être inférieure à 50 Mo");
      return;
    }
    setUploadingBannerVideo(true);
    setError('');
    setMessage('');
    try {
      await uploadHomepageBannerVideo(file);
      setMessage('Bannière vidéo téléchargée avec succès !');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Échec du téléchargement de la vidéo");
    } finally {
      setUploadingBannerVideo(false);
      e.target.value = '';
    }
  };

  const handleBannerVideoDelete = async () => {
    if (!window.confirm("Supprimer la bannière vidéo d'accueil ?")) return;
    setUploadingBannerVideo(true);
    setError('');
    setMessage('');
    try {
      await deleteHomepageBannerVideo();
      setMessage('Bannière vidéo supprimée');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Échec de la suppression');
    } finally {
      setUploadingBannerVideo(false);
    }
  };

  const handleB2bVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      setError('Veuillez sélectionner un fichier vidéo');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setError("La taille de la vidéo doit être inférieure à 50 Mo");
      return;
    }
    setUploadingB2bVideo(true);
    setError('');
    setMessage('');
    try {
      await uploadB2bHeroVideo(file);
      setMessage('Vidéo B2B téléchargée avec succès !');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Échec du téléchargement de la vidéo B2B");
    } finally {
      setUploadingB2bVideo(false);
      e.target.value = '';
    }
  };

  const handleB2bVideoDelete = async () => {
    if (!window.confirm("Supprimer la vidéo de fond B2B ?")) return;
    setUploadingB2bVideo(true);
    setError('');
    setMessage('');
    try {
      await deleteB2bHeroVideo();
      setMessage('Vidéo B2B supprimée');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Échec de la suppression');
    } finally {
      setUploadingB2bVideo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer le logo ?')) {
      return;
    }

    setUploadingLogo(true);
    setError('');
    setMessage('');

    try {
      await deleteLogo();
      setMessage('Logo supprimé avec succès !');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Échec de la suppression du logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');

    try {
      const payload = {
        ...settings,
        free_shipping_threshold:
          settings.free_shipping_threshold === '' || settings.free_shipping_threshold === null
            ? null
            : settings.free_shipping_threshold,
        sendit_pickup_district_price:
          settings.sendit_pickup_district_price === '' || settings.sendit_pickup_district_price === null
            ? null
            : settings.sendit_pickup_district_price,
      };
      await updateCompanySettings(payload);
      setMessage('Paramètres de l\'entreprise enregistrés avec succès !');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Échec de l\'enregistrement des paramètres');
    } finally {
      setSaving(false);
    }
  };

  if (contextLoading) {
    return <div className="loading">Chargement des paramètres...</div>;
  }

  const logoUrl = getLogoUrl();

  return (
    <div>
      <div className="page-header">
        <h1>Paramètres de l'entreprise</h1>
      </div>

      {message && <div className="success-message" style={{marginBottom: '20px', padding: '12px', background: '#d4edda', color: '#155724', borderRadius: '6px'}}>{message}</div>}
      {error && <div className="error-message" style={{marginBottom: '20px', padding: '12px', background: '#f8d7da', color: '#721c24', borderRadius: '6px'}}>{error}</div>}

      {/* Logo Section */}
      <div className="card" style={{marginBottom: '20px'}}>
        <div className="card-header">
          <h2>Logo de l'entreprise</h2>
        </div>
        <div className="card-body">
          {logoUrl && (
            <div style={{marginBottom: '20px', textAlign: 'center'}}>
              <img
                src={logoUrl}
                alt="Logo de l'entreprise"
                style={{maxWidth: '300px', maxHeight: '150px', border: '1px solid #ddd', borderRadius: '8px', padding: '10px'}}
              />
            </div>
          )}
          <div className="form-group">
            <label>Télécharger un nouveau logo</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              disabled={uploadingLogo}
              style={{marginBottom: '10px'}}
            />
            <p style={{fontSize: '12px', color: '#666', margin: '8px 0'}}>
              Taille maximale du fichier : 2 Mo. Formats supportés : JPG, PNG, GIF, SVG
            </p>
            {logoUrl && (
              <button
                type="button"
                onClick={handleDeleteLogo}
                className="btn btn-danger"
                disabled={uploadingLogo}
                style={{marginTop: '10px', background: '#dc3545', color: 'white', border: 'none'}}
              >
                {uploadingLogo ? 'Suppression...' : 'Supprimer le logo'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Homepage Banners Section */}
      <div className="card">
        <div className="card-header">
          <h2>Bannières page d'accueil</h2>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#666' }}>
            Affichées au-dessus de la section "Nos Meilleures Ventes". Laissez vide pour ne rien afficher.
          </p>
        </div>
        <div className="card-body">
          <div className="form-row">
            {/* Banner Image */}
            <div className="form-group">
              <label>Bannière image</label>
              {companySettings?.homepage_banner_image && (
                <div style={{ marginBottom: 12 }}>
                  <img
                    src={companySettings.homepage_banner_image}
                    alt="Bannière actuelle"
                    style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 6, border: '1px solid #e2e8f0', display: 'block' }}
                  />
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleBannerImageUpload}
                disabled={uploadingBannerImage}
                style={{ marginBottom: 8 }}
              />
              <p style={{ fontSize: 12, color: '#666', margin: '4px 0' }}>
                Max 10 Mo · convertie en WebP automatiquement · format conseillé 1920×400 (paysage large)
              </p>
              {companySettings?.homepage_banner_image && (
                <button
                  type="button"
                  onClick={handleBannerImageDelete}
                  className="btn btn-danger"
                  disabled={uploadingBannerImage}
                  style={{ marginTop: 8, background: '#dc3545', color: 'white', border: 'none' }}
                >
                  {uploadingBannerImage ? 'Suppression...' : 'Supprimer la bannière image'}
                </button>
              )}
            </div>

            {/* Banner Video */}
            <div className="form-group">
              <label>Bannière vidéo</label>
              {companySettings?.homepage_banner_video && (
                <div style={{ marginBottom: 12 }}>
                  <video
                    src={companySettings.homepage_banner_video}
                    controls
                    style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 6, border: '1px solid #e2e8f0', display: 'block', background: '#000' }}
                  />
                </div>
              )}
              <input
                type="file"
                accept="video/mp4,video/webm,video/quicktime"
                onChange={handleBannerVideoUpload}
                disabled={uploadingBannerVideo}
                style={{ marginBottom: 8 }}
              />
              <p style={{ fontSize: 12, color: '#666', margin: '4px 0' }}>
                Max 50 Mo · MP4/WebM/MOV · sera lue en boucle, sans son · format conseillé 1920×600
              </p>
              {companySettings?.homepage_banner_video && (
                <button
                  type="button"
                  onClick={handleBannerVideoDelete}
                  className="btn btn-danger"
                  disabled={uploadingBannerVideo}
                  style={{ marginTop: 8, background: '#dc3545', color: 'white', border: 'none' }}
                >
                  {uploadingBannerVideo ? 'Suppression...' : 'Supprimer la bannière vidéo'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* B2B Hero Video Section */}
      <div className="card">
        <div className="card-header">
          <h2>Vidéo de fond — Page B2B</h2>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#666' }}>
            Vidéo affichée en arrière-plan du hero de la page B2B (autoplay, sans son, en boucle).
            Si aucune vidéo n'est uploadée, l'image statique par défaut est utilisée.
          </p>
        </div>
        <div className="card-body">
          <div className="form-group">
            <label>Vidéo de fond B2B</label>
            {companySettings?.b2b_hero_video && (
              <div style={{ marginBottom: 12 }}>
                <video
                  src={companySettings.b2b_hero_video}
                  controls
                  style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 6, border: '1px solid #e2e8f0', display: 'block', background: '#000' }}
                />
              </div>
            )}
            <input
              type="file"
              accept="video/mp4,video/webm,video/quicktime"
              onChange={handleB2bVideoUpload}
              disabled={uploadingB2bVideo}
              style={{ marginBottom: 8 }}
            />
            <p style={{ fontSize: 12, color: '#666', margin: '4px 0' }}>
              Max 50 Mo · MP4/WebM/MOV · format conseillé 1920×1080 paysage · sera lue en boucle, sans son
            </p>
            {companySettings?.b2b_hero_video && (
              <button
                type="button"
                onClick={handleB2bVideoDelete}
                className="btn btn-danger"
                disabled={uploadingB2bVideo}
                style={{ marginTop: 8, background: '#dc3545', color: 'white', border: 'none' }}
              >
                {uploadingB2bVideo ? 'Suppression...' : 'Supprimer la vidéo B2B'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Company Information Section */}
      <div className="card">
        <form onSubmit={handleSubmit}>
          <div className="card-header">
            <h2>Informations de l'entreprise</h2>
          </div>
          <div className="card-body">
            <div className="form-row">
              <div className="form-group">
                <label>Nom de l'entreprise *</label>
                <input
                  type="text"
                  name="company_name"
                  value={settings.company_name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="company_email"
                  value={settings.company_email}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Téléphone</label>
                <input
                  type="text"
                  name="company_phone"
                  value={settings.company_phone}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Site web</label>
                <input
                  type="text"
                  name="company_website"
                  value={settings.company_website}
                  onChange={handleChange}
                  placeholder="https://..."
                />
              </div>
            </div>

            <div className="form-group">
              <label>Adresse</label>
              <input
                type="text"
                name="company_address"
                value={settings.company_address}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Adresse de facturation</label>
              <input
                type="text"
                name="address_de_facturation"
                value={settings.address_de_facturation}
                onChange={handleChange}
                placeholder="Utilisée pour les factures, devis, etc."
              />
              <p style={{fontSize: '12px', color: '#666', margin: '4px 0 0 0'}}>
                Si vide, l'adresse principale sera utilisée
              </p>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Ville</label>
                <input
                  type="text"
                  name="company_city"
                  value={settings.company_city}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Pays</label>
                <input
                  type="text"
                  name="company_country"
                  value={settings.company_country}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Numéro de TVA / Identifiant fiscal</label>
                <input
                  type="text"
                  name="tax_id"
                  value={settings.tax_id}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Affiliation CNSS</label>
                <input
                  type="text"
                  name="affiliation_cnss"
                  value={settings.affiliation_cnss}
                  onChange={handleChange}
                  placeholder="Ex: 1234567"
                />
                <p style={{fontSize: '12px', color: '#666', margin: '4px 0 0 0'}}>
                  Affichée dans le pied de page des documents (devis, facture, BL).
                </p>
              </div>
            </div>

            <div className="form-group">
              <label>Google Maps — URL d'intégration (iframe)</label>
              <textarea
                name="google_maps_embed_url"
                value={settings.google_maps_embed_url}
                onChange={handleChange}
                rows="2"
                placeholder="https://www.google.com/maps/embed?pb=..."
                style={{width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '6px', resize: 'vertical', fontFamily: 'monospace', fontSize: '12px'}}
              />
              <p style={{fontSize: '12px', color: '#666', margin: '4px 0 0 0'}}>
                Sur Google Maps : Partager → Intégrer une carte → copiez uniquement l'attribut <code>src="..."</code> de l'iframe.
                Affiché dans le pied de page du site e-commerce.
              </p>
              {settings.google_maps_embed_url && (
                <div style={{marginTop: '12px', border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden'}}>
                  <iframe
                    src={settings.google_maps_embed_url}
                    title="Aperçu Google Maps"
                    width="100%"
                    height="200"
                    style={{border: 0, display: 'block'}}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Code de devise</label>
                <input
                  type="text"
                  name="currency"
                  value={settings.currency}
                  onChange={handleChange}
                  maxLength="10"
                  placeholder="MAD"
                />
                <p style={{fontSize: '12px', color: '#666', margin: '4px 0 0 0'}}>
                  Par ex. : MAD, EUR, USD, GBP
                </p>
              </div>

              <div className="form-group">
                <label>Symbole de devise *</label>
                <input
                  type="text"
                  name="currency_symbol"
                  value={settings.currency_symbol}
                  onChange={handleChange}
                  maxLength="10"
                  placeholder="DH"
                  required
                />
                <p style={{fontSize: '12px', color: '#666', margin: '4px 0 0 0'}}>
                  Ceci remplacera tous les symboles $ dans le système
                </p>
              </div>
            </div>

          {/* Social Media Section */}
          <div style={{borderTop: '1px solid #e9ecef', marginTop: '24px', paddingTop: '24px'}}>
            <h3 style={{marginBottom: '16px', fontSize: '16px', fontWeight: '600'}}>Réseaux sociaux</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Facebook</label>
                <input
                  type="text"
                  name="facebook_url"
                  value={settings.facebook_url}
                  onChange={handleChange}
                  placeholder="https://facebook.com/votrepage"
                />
              </div>
              <div className="form-group">
                <label>Instagram</label>
                <input
                  type="text"
                  name="instagram_url"
                  value={settings.instagram_url}
                  onChange={handleChange}
                  placeholder="https://instagram.com/votrecompte"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>WhatsApp (numéro sans +)</label>
                <input
                  type="text"
                  name="whatsapp_number"
                  value={settings.whatsapp_number}
                  onChange={handleChange}
                  placeholder="212600000000"
                />
              </div>
              <div className="form-group">
                <label>YouTube</label>
                <input
                  type="text"
                  name="youtube_url"
                  value={settings.youtube_url}
                  onChange={handleChange}
                  placeholder="https://youtube.com/@votrechaine"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>TikTok</label>
                <input
                  type="text"
                  name="tiktok_url"
                  value={settings.tiktok_url}
                  onChange={handleChange}
                  placeholder="https://tiktok.com/@votrecompte"
                />
              </div>
              <div className="form-group"></div>
            </div>
          </div>

          {/* Opening Times Section */}
          <div style={{borderTop: '1px solid #e9ecef', marginTop: '24px', paddingTop: '24px'}}>
            <h3 style={{marginBottom: '16px', fontSize: '16px', fontWeight: '600'}}>Horaires d'ouverture</h3>
            <div style={{display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px'}}>
              {(settings.opening_times || []).map((row, index) => (
                <div key={index} style={{display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap'}}>
                  <input
                    type="text"
                    value={row.days}
                    onChange={(e) => handleOpeningTimeChange(index, 'days', e.target.value)}
                    placeholder="Ex: Lundi - Vendredi"
                    style={{flex: '1', minWidth: '160px', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '6px'}}
                  />
                  <input
                    type="text"
                    value={row.hours}
                    onChange={(e) => handleOpeningTimeChange(index, 'hours', e.target.value)}
                    placeholder="Ex: 09h00 - 19h00"
                    disabled={row.closed}
                    style={{flex: '1', minWidth: '140px', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '6px', opacity: row.closed ? 0.5 : 1}}
                  />
                  <label style={{display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', cursor: 'pointer', fontSize: '14px'}}>
                    <input
                      type="checkbox"
                      checked={row.closed}
                      onChange={(e) => handleOpeningTimeChange(index, 'closed', e.target.checked)}
                    />
                    Fermé
                  </label>
                  <button
                    type="button"
                    onClick={() => removeOpeningTimeRow(index)}
                    style={{padding: '6px 12px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px'}}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addOpeningTimeRow}
              style={{padding: '8px 16px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px'}}
            >
              + Ajouter une ligne
            </button>
          </div>

          {/* Footer Content Section */}
          <div style={{borderTop: '1px solid #e9ecef', marginTop: '24px', paddingTop: '24px'}}>
            <h3 style={{marginBottom: '16px', fontSize: '16px', fontWeight: '600'}}>Contenu du pied de page</h3>

            <div className="form-group">
              <label>Description (sous le logo)</label>
              <textarea
                name="footer_description"
                value={settings.footer_description}
                onChange={handleChange}
                rows="3"
                placeholder="Ex: Votre partenaire de confiance pour les équipements high-tech au Maroc."
                style={{width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '6px', resize: 'vertical'}}
              />
            </div>

            {/* Quick Links */}
            <div style={{marginTop: '20px'}}>
              <h4 style={{marginBottom: '10px', fontSize: '14px', fontWeight: '600'}}>Liens Rapides</h4>
              <div style={{display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px'}}>
                {(settings.footer_quick_links || []).map((link, index) => (
                  <div key={index} style={{display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap'}}>
                    <input
                      type="text"
                      value={link.label}
                      onChange={(e) => handleFooterLinkChange('footer_quick_links', index, 'label', e.target.value)}
                      placeholder="Libellé (ex: À Propos)"
                      style={{flex: '1', minWidth: '150px', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '6px'}}
                    />
                    <input
                      type="text"
                      value={link.url}
                      onChange={(e) => handleFooterLinkChange('footer_quick_links', index, 'url', e.target.value)}
                      placeholder="URL (ex: /about)"
                      style={{flex: '1', minWidth: '150px', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '6px'}}
                    />
                    <button
                      type="button"
                      onClick={() => removeFooterLink('footer_quick_links', index)}
                      style={{padding: '6px 12px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px'}}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => addFooterLink('footer_quick_links')}
                style={{padding: '8px 16px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px'}}
              >
                + Ajouter un lien
              </button>
            </div>

            {/* Footer Categories */}
            <div style={{marginTop: '20px'}}>
              <h4 style={{marginBottom: '10px', fontSize: '14px', fontWeight: '600'}}>Catégories (pied de page)</h4>
              <div style={{display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px'}}>
                {(settings.footer_categories || []).map((link, index) => (
                  <div key={index} style={{display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap'}}>
                    <input
                      type="text"
                      value={link.label}
                      onChange={(e) => handleFooterLinkChange('footer_categories', index, 'label', e.target.value)}
                      placeholder="Libellé (ex: Dash Cams)"
                      style={{flex: '1', minWidth: '150px', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '6px'}}
                    />
                    <input
                      type="text"
                      value={link.url}
                      onChange={(e) => handleFooterLinkChange('footer_categories', index, 'url', e.target.value)}
                      placeholder="URL (ex: /products?category_id=1)"
                      style={{flex: '1', minWidth: '150px', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '6px'}}
                    />
                    <button
                      type="button"
                      onClick={() => removeFooterLink('footer_categories', index)}
                      style={{padding: '6px 12px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px'}}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => addFooterLink('footer_categories')}
                style={{padding: '8px 16px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px'}}
              >
                + Ajouter une catégorie
              </button>
            </div>
          </div>

          {/* Sendit pickup district */}
          <div style={{borderTop: '1px solid #e9ecef', marginTop: '24px', paddingTop: '24px'}}>
            <h3 style={{marginBottom: '6px', fontSize: '16px', fontWeight: '600'}}>Livraison Sendit — Point de ramassage</h3>
            <p style={{fontSize: 12, color: '#666', margin: '0 0 12px'}}>
              Ville où Sendit doit venir récupérer vos colis. Obligatoire pour créer une livraison.
              La sélection affiche le tarif de livraison sortant configuré par Sendit pour chaque ville.
            </p>
            <div style={{maxWidth: 480}}>
              <SenditDistrictPicker
                value={settings.sendit_pickup_district_id}
                valueLabel={settings.sendit_pickup_district_name}
                valuePrice={settings.sendit_pickup_district_price}
                placeholder="Rechercher une ville (ex: Casablanca)…"
                onChange={({ id, name, price }) => {
                  setSettings((prev) => ({
                    ...prev,
                    sendit_pickup_district_id: id || '',
                    sendit_pickup_district_name: name || '',
                    sendit_pickup_district_price: price ?? '',
                  }));
                }}
              />
              {settings.sendit_pickup_district_id && (
                <p style={{fontSize: 12, color: '#0a3a82', margin: '6px 0 0'}}>
                  Ville sélectionnée : <strong>{settings.sendit_pickup_district_name}</strong>
                  {settings.sendit_pickup_district_price !== '' && settings.sendit_pickup_district_price !== null && (
                    <> · Tarif : <strong>{parseFloat(settings.sendit_pickup_district_price).toFixed(2)} DH</strong></>
                  )}
                </p>
              )}
            </div>

            {/* Free shipping threshold */}
            <div style={{marginTop: 20, maxWidth: 480}}>
              <h4 style={{fontSize: 14, fontWeight: 600, margin: '0 0 6px'}}>Livraison gratuite (optionnel)</h4>
              <p style={{fontSize: 12, color: '#666', margin: '0 0 8px'}}>
                Montant minimum de commande (sous-total après remise) à partir duquel la livraison devient gratuite.
                Laissez vide pour facturer toujours la livraison au tarif Sendit.
              </p>
              <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="free_shipping_threshold"
                  value={settings.free_shipping_threshold}
                  onChange={handleChange}
                  placeholder="Ex: 500"
                  style={{width: 200, padding: '8px 12px', border: '1px solid #ced4da', borderRadius: 6}}
                />
                <span style={{color: '#666', fontSize: 13}}>DH</span>
                {settings.free_shipping_threshold !== '' && (
                  <button
                    type="button"
                    onClick={() => setSettings((prev) => ({...prev, free_shipping_threshold: ''}))}
                    style={{padding: '6px 10px', background: '#6c757d', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12}}
                  >
                    Désactiver
                  </button>
                )}
              </div>
            </div>

          </div>

          {/* Tracking Pixels Section */}
          <div style={{borderTop: '1px solid #e9ecef', marginTop: '24px', paddingTop: '24px'}}>
            <h3 style={{marginBottom: '6px', fontSize: '16px', fontWeight: '600'}}>Pixels de suivi / Analytics</h3>
            <p style={{fontSize: 12, color: '#666', margin: '0 0 16px'}}>
              Ajoutez les pixels Meta, Google Analytics, GTM, TikTok ou Snapchat. Choisissez sur quelles pages les injecter.
              Pour un script personnalisé, utilisez "HTML personnalisé".
            </p>

            <div style={{display: 'flex', flexDirection: 'column', gap: 14}}>
              {(settings.tracking_pixels || []).map((p, idx) => {
                const providerCfg = PIXEL_PROVIDERS.find((x) => x.value === p.provider) || PIXEL_PROVIDERS[0];
                const isCustom = p.provider === 'custom';
                const pages = Array.isArray(p.pages) ? p.pages : [];
                return (
                  <div
                    key={p.id || idx}
                    style={{
                      border: '1px solid #e2e8f0', borderRadius: 8, padding: 14,
                      background: p.enabled === false ? '#f8f9fa' : '#fff',
                      opacity: p.enabled === false ? 0.7 : 1,
                    }}
                  >
                    {/* Top row: name + provider + enabled toggle + remove */}
                    <div style={{display: 'grid', gridTemplateColumns: '1.4fr 1.6fr auto auto', gap: 10, alignItems: 'end', marginBottom: 10}}>
                      <div>
                        <label style={{fontSize: 12, fontWeight: 600, color: '#444', display: 'block', marginBottom: 4}}>Nom interne</label>
                        <input
                          type="text"
                          value={p.name || ''}
                          onChange={(e) => updatePixel(idx, 'name', e.target.value)}
                          placeholder="Ex: Meta Pixel principal"
                          style={{width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: 6}}
                        />
                      </div>
                      <div>
                        <label style={{fontSize: 12, fontWeight: 600, color: '#444', display: 'block', marginBottom: 4}}>Fournisseur</label>
                        <select
                          value={p.provider}
                          onChange={(e) => updatePixel(idx, 'provider', e.target.value)}
                          style={{width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: 6}}
                        >
                          {PIXEL_PROVIDERS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <label style={{display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, whiteSpace: 'nowrap', cursor: 'pointer'}}>
                        <input
                          type="checkbox"
                          checked={p.enabled !== false}
                          onChange={(e) => updatePixel(idx, 'enabled', e.target.checked)}
                        />
                        Actif
                      </label>
                      <button
                        type="button"
                        onClick={() => removePixel(idx)}
                        title="Supprimer ce pixel"
                        style={{padding: '8px 12px', background: '#dc3545', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer'}}
                      >
                        ×
                      </button>
                    </div>

                    {/* ID input (non-custom) or HTML editors (custom) */}
                    {!isCustom ? (
                      <div style={{marginBottom: 10}}>
                        <label style={{fontSize: 12, fontWeight: 600, color: '#444', display: 'block', marginBottom: 4}}>
                          ID du pixel *
                        </label>
                        <input
                          type="text"
                          value={p.pixel_id || ''}
                          onChange={(e) => updatePixel(idx, 'pixel_id', e.target.value)}
                          placeholder={providerCfg.placeholder}
                          style={{width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: 6, fontFamily: 'monospace'}}
                        />
                      </div>
                    ) : (
                      <>
                        <div style={{marginBottom: 10}}>
                          <label style={{fontSize: 12, fontWeight: 600, color: '#444', display: 'block', marginBottom: 4}}>
                            HTML à injecter dans le &lt;head&gt;
                          </label>
                          <textarea
                            value={p.custom_head || ''}
                            onChange={(e) => updatePixel(idx, 'custom_head', e.target.value)}
                            rows="4"
                            placeholder='<script>...</script>'
                            style={{width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: 6, fontFamily: 'monospace', fontSize: 12, resize: 'vertical'}}
                          />
                        </div>
                        <div style={{marginBottom: 10}}>
                          <label style={{fontSize: 12, fontWeight: 600, color: '#444', display: 'block', marginBottom: 4}}>
                            HTML à injecter dans le &lt;body&gt; (optionnel)
                          </label>
                          <textarea
                            value={p.custom_body || ''}
                            onChange={(e) => updatePixel(idx, 'custom_body', e.target.value)}
                            rows="3"
                            placeholder='<noscript>...</noscript>'
                            style={{width: '100%', padding: '8px 12px', border: '1px solid #ced4da', borderRadius: 6, fontFamily: 'monospace', fontSize: 12, resize: 'vertical'}}
                          />
                        </div>
                      </>
                    )}

                    {/* Pages selector */}
                    <div>
                      <label style={{fontSize: 12, fontWeight: 600, color: '#444', display: 'block', marginBottom: 6}}>
                        Injecter sur ces pages :
                      </label>
                      <div style={{display: 'flex', flexWrap: 'wrap', gap: 8}}>
                        {PIXEL_PAGES.map((opt) => {
                          const checked = pages.includes(opt.value);
                          return (
                            <label
                              key={opt.value}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px',
                                border: '1px solid ' + (checked ? '#0d6efd' : '#ced4da'),
                                background: checked ? '#cfe2ff' : '#fff',
                                color: checked ? '#0a3a82' : '#444',
                                borderRadius: 99, fontSize: 12, cursor: 'pointer',
                                fontWeight: opt.value === 'all' ? 600 : 400,
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => togglePixelPage(idx, opt.value)}
                                style={{margin: 0}}
                              />
                              {opt.label}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={addPixel}
              style={{marginTop: 14, padding: '8px 16px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14}}
            >
              + Ajouter un pixel
            </button>
          </div>

          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Enregistrement...' : 'Enregistrer les paramètres'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CompanySettings;
